import React, { useEffect } from 'react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Center, Flex, Heading, Image, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createCustomWifiIcon } from 'clipsal-cortex-icons/src/custom-dynamic-icons';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';
import { useViewportType } from 'clipsal-cortex-utils/src/hooks/use-viewport-type';

import { useReduxDispatch } from '../../../app/store';
import chargerActiveImg from '../../../assets/images/evse_charger_active.svg';
import chargerInactiveImg from '../../../assets/images/evse_charger_inactive.svg';
import chargerOfflineImg from '../../../assets/images/evse_charger_status_offline.svg';
import chargerOnlineImg from '../../../assets/images/evse_charger_status_online.svg';
import { ArcButton } from '../../../common/components/ArcButton';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../common/hooks/use-param-back-url';
import { deactivateEvCharger, fetchEvChargerDetails, selectEvChargerData } from './evChargerSlice';

/* istanbul ignore next -- @preserve */
export const DeviceDetails = () => {
  const {
    siteDeviceId,
    charger_id: chargerId,
    isDeactivating,
    hasFetchedEvChargerDetails,
    firmware,
    model,
    sn,
    status,
  } = useSelector(selectEvChargerData);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { isDesktopViewport } = useViewportType();
  const backURL = useParamBackUrl('../../../account/hardware/list');

  const chargerStatusImg = status === 'Available' ? chargerOnlineImg : chargerOfflineImg;
  const chargerImg = status === 'Available' ? chargerActiveImg : chargerInactiveImg;
  const WifiIcon = createCustomWifiIcon(undefined, '');

  useEffect(() => {
    if (!hasFetchedEvChargerDetails && siteDeviceId) {
      dispatch(fetchEvChargerDetails(siteDeviceId));
    }
  }, [hasFetchedEvChargerDetails, siteDeviceId, dispatch]);

  function handleRemoveDevice() {
    if (status === 'Charging') {
      toast({
        title: t('EVSE.cannot remove charger'),
        description: t('EVSE.cannot remove charger description'),
        status: 'error',
        isClosable: true,
      });
      return;
    }

    // Open confirmation dialog.
    onOpen();
  }

  return (
    <SlidingAnimationPageBase title={t('Hardware.hardware')} backURL={backURL} customNavigationDirection="forward">
      {!hasFetchedEvChargerDetails ? (
        <CenteredLoader />
      ) : (
        <Flex direction={'column'} w="100%" data-testid="device-details-screen">
          <Center flexDirection="column" px={3} pb={6} textAlign="center">
            <Box position={'relative'}>
              <Image src={chargerStatusImg} alt="Charging Station Setup Successful" mb={4} />
              <Image
                src={chargerImg}
                alt="Charging Station"
                position={'absolute'}
                right={84}
                bottom={70}
                onClick={() => navigate('../device-status')}
              />
            </Box>

            <Heading mt={2}>{t('Set Up Hardware.charging station')}</Heading>
            <Text mt={1}>
              {status === 'Available' ? t('Set Up Hardware.charger connected') : t('EVSE.charger not connected')}
            </Text>
          </Center>

          <Flex flexDirection="column" px={4}>
            <Flex mb={1} align="Center" justify="space-between">
              <Box>{t('Hardware.connection')}</Box>
            </Flex>
            <SEHomeCard borderTopLeftRadius={5} borderBottomRightRadius={'25px'} textAlign={'center'}>
              <Flex
                w="100%"
                py={2}
                as="button"
                onClick={() => navigate('../device-wifi-configuration')}
                data-testid="wifi-config-button"
              >
                <WifiIcon boxSize={5} mr={3} />
                <Text>{t('EVSE.wifi')}</Text>
                <ChevronRightIcon color={'customGrey.400'} ml="auto" w={6} h={6} />
              </Flex>
            </SEHomeCard>
          </Flex>

          <Flex direction={'column'} p={6} mx={isDesktopViewport ? 4 : 0} borderRadius={isDesktopViewport ? 4 : 0}>
            <Flex direction={'column'} data-testid="device-model-name">
              <Text fontWeight={'semibold'}>{t('Common.model name')}</Text>
              <Text>{t('Set Up Hardware.ev charger')}</Text>
            </Flex>
            <Flex direction={'column'} mt={6} data-testid="device-id">
              <Text fontWeight={'semibold'}>{t('Set Up Hardware.charger id')}</Text>
              <Text>{chargerId}</Text>
            </Flex>
            {model && (
              <Flex direction={'column'} mt={6} data-testid="device-model">
                <Text fontWeight={'semibold'}>{t('Common.model number')}</Text>
                <Text>{model}</Text>
              </Flex>
            )}
            <Flex direction={'column'} mt={6} data-testid="device-sn">
              <Text fontWeight={'semibold'}>{t('Common.serial number')}</Text>
              <Text>{sn}</Text>
            </Flex>
            <Flex direction={'column'} mt={6} data-testid="device-manufacturer">
              <Text fontWeight={'semibold'}>{t('Hardware.manufacturer')}</Text>
              <Text>{t('Set Up Hardware.schneider electric')}</Text>
            </Flex>
            <Flex direction={'column'} mt={6} data-testid="device-firmware">
              <Text fontWeight={'semibold'}>{t('Common.firmware')}</Text>
              <Text>{firmware}</Text>
            </Flex>
          </Flex>

          <ArcButton
            arcColor="#3DCD58"
            data-testid="device-remove-button"
            minWidth="280px"
            mx="auto"
            mt={10}
            py={6}
            onClick={handleRemoveDevice}
            isLoading={isDeactivating}
          >
            {t('Set Up Hardware.remove device')}
          </ArcButton>

          <AlertDialogModal
            {...{ isOpen, onClose }}
            header={t('Set Up Hardware.remove charger?')}
            subHeader={t('Set Up Hardware.remove charger confirmation')}
            confirmButtonName={t('Common.remove')}
            onConfirm={async () => {
              try {
                unwrapResult(await dispatch(deactivateEvCharger(siteDeviceId)));
                navigate('../device-removed');
              } catch (error) {
                toast({
                  title: t('Set Up Hardware.error deactivating charger'),
                  description: t('Common.please contact support'),
                  status: 'error',
                  isClosable: true,
                });
              }
            }}
            dialogContentTestId="device-remove-dialog"
            cancelButtonTestId="device-dialog-cancel-button"
            confirmButtonTestId="device-dialog-remove-button"
          />
        </Flex>
      )}
    </SlidingAnimationPageBase>
  );
};
